import type { ProvenanceRef, ReportValue, ValueStatus, PublicationStatus } from './types';

export function reportValue<T>(
  value: T | null | undefined,
  unit: string,
  status: ValueStatus,
  provenance: ProvenanceRef,
  publicationStatus: PublicationStatus = 'provisional'
): ReportValue<T> {
  const isMissing =
    status === 'missing' ||
    status === 'not_modeled' ||
    status === 'not_applicable' ||
    status === 'unresolved_breakout';
  return {
    value: isMissing ? null : (value ?? null),
    unit,
    status,
    provenance,
    publicationStatus,
  };
}

/** Conservative mapping from documented source/status fields. */
export function mapDocumentedSourceStatus(source: string | null | undefined): ValueStatus {
  if (!source || !String(source).trim()) return 'missing';
  const s = String(source).trim().toLowerCase();
  if (s === 'observed') return 'observed';
  if (s === 'interpolated') return 'interpolated';
  if (s === 'extrapolated' || s === 'extrapolated_forward') return 'extrapolated';
  if (s.includes('backcast')) return 'extrapolated';
  if (s === 'modeled' || s === 'calculated' || s === 'database_direct') return 'modeled';
  // Unknown documented label — treat as modeled but provisional at publication layer
  return 'modeled';
}

export function emissionsStatus(
  emissions: number | null,
  sourceField: string | null | undefined
): { status: ValueStatus; publicationStatus: PublicationStatus } {
  if (emissions == null) return { status: 'missing', publicationStatus: 'provisional' };
  if (!sourceField || !String(sourceField).trim()) {
    return { status: 'modeled', publicationStatus: 'descriptive_only' };
  }
  const mapped = mapDocumentedSourceStatus(sourceField);
  const publicationStatus: PublicationStatus =
    mapped === 'modeled' && !['observed', 'interpolated', 'extrapolated'].includes(mapped)
      ? 'descriptive_only'
      : 'provisional';
  return { status: mapped, publicationStatus };
}

export function numOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
